import { useCallback, useEffect, useState } from "react";

import { API_PROJECT_ENROLLMENT_REQUESTS } from "../../../config/Endpoints.js";
import { getApi, putApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { formatLocalDate } from "../../utils/helpers.ts";
import type {
  EnrollmentRequestDetail,
  EnrollmentRequestFilters,
  EnrollmentRequestRow,
  EnrollmentRequestStatus,
} from "./project-enrollment-requests-types.ts";

type PageResponse = {
  content?: EnrollmentRequestRow[];
  totalElements?: number;
};

function unwrap<T>(value: T | { data?: T }): T {
  if (value && typeof value === "object" && "data" in value) {
    return (value as { data?: T }).data ?? (value as T);
  }
  return value as T;
}

export function useProjectEnrollmentRequests(
  filters: EnrollmentRequestFilters,
  page: number,
  pageSize: number,
) {
  const [rows, setRows] = useState<EnrollmentRequestRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const refetch = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({
      page: String(Math.max(0, page - 1)),
      size: String(pageSize),
    });
    if (filters.employeeId) params.set("employeeId", filters.employeeId);
    if (filters.status) params.set("status", filters.status);
    if (filters.startDate) params.set("startDate", formatLocalDate(filters.startDate));
    if (filters.endDate) params.set("endDate", formatLocalDate(filters.endDate));

    setLoading(true);
    setError(null);
    getApi<PageResponse | { data?: PageResponse }>(
      `${API_PROJECT_ENROLLMENT_REQUESTS}?${params.toString()}`,
      accessToken(),
      "json",
      controller.signal,
    )
      .then(({ data }) => {
        const payload = unwrap(data);
        setRows(Array.isArray(payload.content) ? payload.content : []);
        setTotalElements(Number(payload.totalElements) || 0);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setRows([]);
          setTotalElements(0);
          setError(reason instanceof Error ? reason.message : "Failed to load enrollment requests.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [filters, page, pageSize, revision]);

  return { rows, totalElements, loading, error, refetch };
}

export async function getEnrollmentRequestDetail(id: number) {
  const { data } = await getApi<EnrollmentRequestDetail | { data?: EnrollmentRequestDetail }>(
    `${API_PROJECT_ENROLLMENT_REQUESTS}/${id}`,
    accessToken(),
  );
  return unwrap(data);
}

export async function decideEnrollmentRequest(
  id: number,
  status: Extract<EnrollmentRequestStatus, "APPROVED" | "REJECTED">,
  comment: string,
) {
  const { data } = await putApi<EnrollmentRequestDetail | { data?: EnrollmentRequestDetail }>(
    `${API_PROJECT_ENROLLMENT_REQUESTS}/${id}/decision`,
    accessToken(),
    { status, comment },
  );
  return unwrap(data);
}
